import type { CaptureReadinessContract } from "@chams-pay/contracts";

const MINIMUM_STORAGE_HEADROOM = 10 * 1024 * 1024;

export interface BrowserStorageManager {
  readonly persist?: () => Promise<boolean>;
  readonly persisted?: () => Promise<boolean>;
  readonly estimate?: () => Promise<{ readonly quota?: number; readonly usage?: number }>;
}

export interface BrowserStorageReadiness {
  readonly persistenceGranted: boolean;
  readonly storageHeadroom: boolean;
}

/**
 * Request persistence immediately when entered from a user action. Some
 * browsers will not grant it after the later network awaits have completed.
 */
export async function requestBrowserStorageReadiness(
  forceReady = false,
  storage: BrowserStorageManager | undefined = navigator.storage,
): Promise<BrowserStorageReadiness> {
  if (forceReady) return { persistenceGranted: true, storageHeadroom: true };
  if (!storage) return { persistenceGranted: false, storageHeadroom: false };

  let persistenceGranted = false;
  try {
    persistenceGranted = (await storage.persist?.()) === true;
  } catch {
    // A denied or unsupported request remains an explicit readiness failure.
  }
  if (!persistenceGranted) {
    try {
      persistenceGranted = (await storage.persisted?.()) === true;
    } catch {
      // A denied or unsupported query remains an explicit readiness failure.
    }
  }

  let storageHeadroom = false;
  try {
    const estimate = await storage.estimate?.();
    storageHeadroom = (estimate?.quota ?? 0) - (estimate?.usage ?? 0) >= MINIMUM_STORAGE_HEADROOM;
  } catch {
    // Unknown capacity fails closed.
  }
  return { persistenceGranted, storageHeadroom };
}

export type CaptureBlockReason = "lease" | "storage" | "clock" | "safety" | null;

export function captureBlockReason(
  readiness: CaptureReadinessContract | null,
  syncAgeMilliseconds: number,
  leaseBlockMilliseconds: number,
): CaptureBlockReason {
  if (readiness?.code === "LEASE_EXPIRED" || syncAgeMilliseconds > leaseBlockMilliseconds) {
    return "lease";
  }
  if (
    readiness?.code === "PERSISTENCE_REQUIRED" ||
    readiness?.code === "STORAGE_HEADROOM_REQUIRED" ||
    readiness?.code === "DURABILITY_TEST_REQUIRED"
  ) {
    return "storage";
  }
  if (readiness?.code === "CLOCK_UNCERTAIN") return "clock";
  return readiness?.allowed === false ? "safety" : null;
}
